/* eslint-disable max-lines */

import { useState } from "react";
import { aiImageService } from "../aiImageService";
import { useDesignOptions } from "../contexts/DesignOptionsContext";
import {
  trackAiStudioDesignsGenerated,
  trackAiStudioGeneratedDesignChosen,
  trackAiStudioGenerateDesignPromptSubmitted,
  trackAiStudioRefinedDesignChosen,
  trackAiStudioRefinedDesignGenerated,
  trackAiStudioRefineDesignCanceled,
  trackAiStudioRefineDesignCtaClicked,
  trackAiStudioRefineDesignPromptSubmitted
} from "../lib/analytics";
import { uploadImageToDtf } from "../services/imageUploadService";
import type { GeneratedImage, GenerateImageParams, RefineImageParams } from "../types";

export const useImageGeneration = (productCatalogNumber?: string | null) => {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const designOptions = useDesignOptions();

  // Refine functionality state
  const [editingImage, setEditingImage] = useState<number | null>(null);
  const [uploadedImageForEditing, setUploadedImageForEditing] = useState<GeneratedImage | null>(
    null
  );
  const [isRefining, setIsRefining] = useState(false);
  const [imageTransitioning, setImageTransitioning] = useState(false);
  const [selectingImageUrl, setSelectingImageUrl] = useState<string | null>(null);

  const generateImages = async (params: GenerateImageParams) => {
    setIsGenerating(true);
    setGeneratedImages([]);
    setError(null);

    trackAiStudioGenerateDesignPromptSubmitted({
      prompt: params.prompt,
      designType: params.selectedTemplate ?? "default",
      artStyle: params.selectedStyle ?? "default",
      orientation: params.selectedOrientation,
      colorMode: params.selectedColors
    });

    try {
      const startTime = Date.now();
      const images = await aiImageService.generateImages(params);
      setGeneratedImages(images);

      trackAiStudioDesignsGenerated({
        duration: Date.now() - startTime,
        prompt: params.prompt,
        urls: images.map(img => img.url),
        designType: params.selectedTemplate ?? "default",
        artStyle: params.selectedStyle ?? "default",
        orientation: params.selectedOrientation,
        colorMode: params.selectedColors
      });
    } catch (err) {
      console.error("Error generating images:", err);
      setError(
        "We encountered an issue generating your images. This might be due to API limits or connectivity issues. Please try again with a different prompt or check back later."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const selectImage = async (imageUrl: string, refinementPrompt?: string) => {
    const selectedImage =
      uploadedImageForEditing?.url === imageUrl
        ? uploadedImageForEditing
        : generatedImages.find(img => img.url === imageUrl);

    if (!selectedImage) {
      setError("Selected image not found");
      return;
    }

    const commonParams = {
      designType: designOptions.selectedTemplate ?? "default",
      artStyle: designOptions.selectedStyle ?? "default",
      orientation: designOptions.selectedOrientation,
      colorMode: designOptions.selectedColors
    };
    if (editingImage === null) {
      trackAiStudioGeneratedDesignChosen({
        ...commonParams,
        prompt,
        chosenDesignUrl: imageUrl,
        provider: selectedImage.provider
      });
    } else {
      trackAiStudioRefinedDesignChosen({
        ...commonParams,
        originalDesignUrl: imageUrl,
        refinedPrompt: refinementPrompt ?? "N/A",
        originalPrompt: prompt
      });
    }

    try {
      setSelectingImageUrl(imageUrl);
      setError(null);

      const result = await uploadImageToDtf(
        selectedImage.imageVersionId,
        selectedImage.url,
        productCatalogNumber
      );

      if (result.redirect_url) {
        window.location.href = result.redirect_url;
      } else {
        alert("Image uploaded successfully to DTF artworks!");
      }
    } catch (err) {
      console.error("Error uploading image:", err);
      setError(`${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setSelectingImageUrl(null);
    }
  };

  const startEditing = (index: number) => {
    const chosenDesignUrl = generatedImages[index]?.url;

    if (chosenDesignUrl) {
      trackAiStudioRefineDesignCtaClicked({
        originalDesignUrl: chosenDesignUrl,
        originalPrompt: prompt,
        designType: designOptions.selectedTemplate ?? "default",
        artStyle: designOptions.selectedStyle ?? "default",
        orientation: designOptions.selectedOrientation,
        colorMode: designOptions.selectedColors
      });
    }
    setEditingImage(index);
    setUploadedImageForEditing(null);
    setError(null);
  };

  const startEditingUploadedImage = (imageUrl: string) => {
    const uploadedImage: GeneratedImage = {
      url: imageUrl,
      imageVersionId: "",
      provider: ""
    };
    setUploadedImageForEditing(uploadedImage);
    setEditingImage(null);
    setError(null);
  };

  const exitEditing = () => {
    const chosenDesignUrl =
      editingImage !== null ? generatedImages[editingImage]?.url : uploadedImageForEditing?.url;

    if (chosenDesignUrl) {
      trackAiStudioRefineDesignCanceled({
        originalDesignUrl: chosenDesignUrl,
        originalPrompt: prompt,
        designType: designOptions.selectedTemplate ?? "default",
        artStyle: designOptions.selectedStyle ?? "default",
        orientation: designOptions.selectedOrientation,
        colorMode: designOptions.selectedColors
      });
    }

    setEditingImage(null);
    setUploadedImageForEditing(null);
    setError(null);
  };

  const refineImage = async (refinementPrompt: string) => {
    if (!refinementPrompt.trim()) return;

    const currentImageToRefine =
      editingImage !== null ? generatedImages[editingImage] : uploadedImageForEditing;

    if (!currentImageToRefine) return;

    setIsRefining(true);
    setError(null);

    if (designOptions) {
      trackAiStudioRefineDesignPromptSubmitted({
        originalDesignUrl: currentImageToRefine.url,
        originalPrompt: prompt,
        refinedPrompt: refinementPrompt,
        designType: designOptions.selectedTemplate ?? "default",
        artStyle: designOptions.selectedStyle ?? "default",
        orientation: designOptions.selectedOrientation,
        colorMode: designOptions.selectedColors
      });
    }

    try {
      const params: RefineImageParams = {
        refinementPrompt,
        inputImageUrl: currentImageToRefine.url
      };

      const startTime = Date.now();
      const refinedImage = await aiImageService.refineImage(params);

      if (refinedImage) {
        const newImage = new window.Image();
        newImage.onload = () => {
          setImageTransitioning(true);
          setTimeout(() => {
            if (editingImage !== null) {
              // Update generated image
              const updatedImages = [...generatedImages];
              updatedImages[editingImage] = refinedImage;
              setGeneratedImages(updatedImages);
            } else {
              // Update uploaded image
              setUploadedImageForEditing(refinedImage);
            }
            setImageTransitioning(false);

            if (designOptions) {
              trackAiStudioRefinedDesignGenerated({
                duration: Date.now() - startTime,
                refinedDesignUrl: refinedImage.url,
                originalDesignUrl: currentImageToRefine.url,
                refinedPrompt: refinementPrompt,
                originalPrompt: prompt,
                designType: designOptions.selectedTemplate ?? "default",
                artStyle: designOptions.selectedStyle ?? "default",
                orientation: designOptions.selectedOrientation,
                colorMode: designOptions.selectedColors
              });
            }
          }, 300);
        };
        newImage.onerror = () => {
          setError("Failed to load refined image. Please try again.");
        };
        newImage.src = refinedImage.url;
      } else {
        setError("Failed to generate refined image. Please try again.");
      }
    } catch (err) {
      console.error("Error refining image:", err);
      setError(
        "We encountered an issue refining your image. Please try again with a different prompt."
      );
    } finally {
      setIsRefining(false);
    }
  };

  const clearError = () => setError(null);

  return {
    // State
    prompt,
    isGenerating,
    generatedImages,
    error,
    editingImage,
    uploadedImageForEditing,
    isRefining,
    imageTransitioning,
    selectingImageUrl,

    // Actions
    setPrompt,
    generateImages,
    selectImage,
    startEditing,
    startEditingUploadedImage,
    exitEditing,
    refineImage,
    clearError,
    setError
  };
};
